import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { registerUser } from '@/redux/user/user.thunks';
import { Button } from '@/shared/Button';
import { Input } from '@/shared/Input';
import React from 'react';
import { adminEmailValSelector } from '@/redux/configuration/configuration.selectors';
import { closeModal } from '@/redux/modal/modal.slice';
import { showToast } from '@/utils/showToast';
import { MIN_PASSWORD_LENGTH } from '@/constants/user';
import { getPasswordError } from '@/utils/password/getPasswordError';

export const RegisterModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const adminEmail = useAppSelector(adminEmailValSelector);
  const [formData, setFormData] = React.useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const passwordError = getPasswordError(formData.password);
    if (passwordError) {
      showToast({ type: 'error', message: passwordError });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showToast({ type: 'error', message: 'Passwords do not match.' });
      return;
    }

    await dispatch(
      registerUser({
        login: formData.email,
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        password: formData.password,
        adminEmail,
      }),
    );
    dispatch(closeModal());
  };

  return (
    <div className="w-[400px] border border-t-[#E1E0E6] bg-white p-[24px]">
      <form onSubmit={handleSubmit}>
        <label className="mb-5 block text-sm font-semibold" htmlFor="name">
          Name:
          <Input
            type="text"
            name="name"
            id="name"
            placeholder="Your name here.."
            value={formData.name}
            onChange={handleChange}
            required
            className="mt-2.5 text-sm font-medium text-font-400"
          />
        </label>
        <label className="mb-5 block text-sm font-semibold" htmlFor="surname">
          Surname:
          <Input
            type="text"
            name="surname"
            id="surname"
            placeholder="Your surname here.."
            value={formData.surname}
            onChange={handleChange}
            required
            className="mt-2.5 text-sm font-medium text-font-400"
          />
        </label>
        <label className="mb-5 block text-sm font-semibold" htmlFor="email">
          Email:
          <Input
            type="email"
            name="email"
            id="email"
            placeholder="Your email here.."
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-2.5 text-sm font-medium text-font-400"
          />
        </label>
        <label className="mb-5 block text-sm font-semibold" htmlFor="password">
          Password:
          <Input
            type="password"
            name="password"
            id="password"
            placeholder="Your password here.."
            value={formData.password}
            onChange={handleChange}
            required
            minLength={MIN_PASSWORD_LENGTH}
            className="mt-2.5 text-sm font-medium text-font-400"
          />
        </label>
        <label className="mb-10 block text-sm font-semibold" htmlFor="confirmPassword">
          Confirm password:
          <Input
            type="password"
            name="confirmPassword"
            id="confirmPassword"
            placeholder="Repeat your password.."
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            className="mt-2.5 text-sm font-medium text-font-400"
          />
        </label>
        <Button type="submit" className="w-full justify-center text-base font-medium">
          Register
        </Button>
      </form>
    </div>
  );
};
